import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { porMes } from '../../utils/agregaciones';
import { money, moneyCorto } from '../../utils/format';

/**
 * Total de propinas por mes, últimos 12.
 *
 * Barras y no línea: los meses son acumulados discretos. Una línea sugeriría
 * que entre marzo y abril hay valores intermedios, y no los hay.
 *
 * NO depende del filtro de fechas de la pantalla: con el filtro en "mes
 * actual" este gráfico mostraría una sola barra y no serviría para nada. Su
 * horizonte es fijo, que es justo lo que lo hace comparable.
 *
 * El mes en curso va apagado y rayado: le faltan días, así que ponerlo a la
 * par de meses completos haría ver una caída que no existe.
 */
export default function GraficoMeses({ history = [], meses = 12 }) {
  const datos = useMemo(() => porMes(history, meses), [history, meses]);

  const hayDatos = datos.some((m) => m.total > 0);
  if (!hayDatos) {
    return (
      <div className="si-caption py-8 text-center">
        Todavía no hay cierres para comparar entre meses.
      </div>
    );
  }

  const chartData = {
    labels: datos.map((m) => m.etiqueta),
    datasets: [
      {
        data: datos.map((m) => m.total),
        // El color no codifica ranking (eso sería mentir con el color), sólo
        // distingue un estado: "este mes todavía no terminó".
        backgroundColor: datos.map((m) =>
          m.parcial ? 'rgba(178, 140, 245, 0.28)' : '#b28cf5'
        ),
        borderColor: datos.map((m) =>
          m.parcial ? 'rgba(178, 140, 245, 0.75)' : '#b28cf5'
        ),
        borderWidth: datos.map((m) => (m.parcial ? 1.5 : 0)),
        borderRadius: 4,
        // Sólo se redondea la punta: la base queda apoyada en el eje.
        borderSkipped: 'bottom',
        // Deja 2px de aire entre barras sin separarlas de más.
        categoryPercentage: 0.75,
        barPercentage: 0.92,
      },
    ],
  };

  const opciones = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      // Una sola serie: el título de la tarjeta ya la nombra.
      legend: { display: false },
      tooltip: {
        backgroundColor: '#17171d',
        borderColor: '#26262e',
        borderWidth: 1,
        titleColor: '#f5f5f7',
        bodyColor: '#c7c7cf',
        padding: 10,
        callbacks: {
          label: (ctx) => {
            const m = datos[ctx.dataIndex];
            const base = `${money(m.total)} · ${m.cierres} cierre${m.cierres === 1 ? '' : 's'}`;
            if (m.enCurso) return `${base} (mes en curso)`;
            if (m.parcial) return `${base} (mes incompleto)`;
            return base;
          },
        },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: '#8a8a94' } },
      y: {
        grid: { color: 'rgba(38, 38, 46, 0.5)' },
        // Pocas marcas: el eje es una referencia, no una tabla. Con nueve
        // líneas la grilla compite con las barras.
        ticks: { color: '#8a8a94', maxTicksLimit: 6, callback: (v) => moneyCorto(v) },
        beginAtZero: true,
      },
    },
  };

  const hayParcial = datos.some((m) => m.parcial);

  return (
    <>
      <div style={{ height: 300 }}>
        <Bar data={chartData} options={opciones} />
      </div>
      {hayParcial && (
        <p className="si-caption mt-3 flex items-center gap-2">
          <span
            aria-hidden="true"
            className="inline-block w-3 h-3 rounded-sm"
            style={{
              background: 'rgba(178, 140, 245, 0.28)',
              border: '1.5px solid rgba(178, 140, 245, 0.75)',
            }}
          />
          Las barras apagadas son meses incompletos: no se pueden comparar de igual a igual.
        </p>
      )}
    </>
  );
}
